/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { Address } from '@msdyn365-commerce/retail-proxy';
import { GetCustomerInput, getCustomer } from '@msdyn365-commerce-modules/retail-actions';

/**
 *  Input class for the getAddress data action.
 */
export class AddressInput implements IActionInput {
    public userAccountNumber?: string;
    public isUpdateObj?: boolean;
    public filteredAddressObj?: Address[];

    public readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings, userAccountNumber?: string, isUpdateObj?: boolean, filteredAddressObj?: Address[]) {
        this.userAccountNumber = userAccountNumber;
        this.isUpdateObj = isUpdateObj || false;
        this.filteredAddressObj = filteredAddressObj || [];
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () => `DEFAULT`;

    public getCacheObjectType = () => 'Get Custom Address';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the getAddress method.
 * @param inputData The input data passed to the createInput method.
 */
export const createGetAddressInput = (inputData: ICreateActionContext): IActionInput => {
    const { requestContext } = inputData;
    if (!requestContext.user.isAuthenticated) {
        throw new Error('Unable to create address input.  User is not authenticated.');
    }

    return new AddressInput(inputData.requestContext.apiSettings);
};

/**
 * The action method for the getAddress data action.
 * @param input The action input.
 * @param ctx The action context.
 */
export async function getAddressAction(input: AddressInput, ctx: IActionContext): Promise<Address[]> {
    if (input.isUpdateObj && input.filteredAddressObj && input.filteredAddressObj.length) {
        return input.filteredAddressObj || [];
    }

    const customerInput = new GetCustomerInput(ctx.requestContext.apiSettings, input.userAccountNumber);

    const customer = await getCustomer(customerInput, ctx);

    if (!customer) {
        throw new Error('Not able to get customer');
    }

    const addressArr: any = customer.Addresses;

    let hasArrHaveAttentionToVal = false;

    addressArr.forEach((item: Address) => {
        if (item.AttentionTo && item.AttentionTo === ctx.requestContext.channel?.ChannelNaturalId) {
            hasArrHaveAttentionToVal = true;
            return;
        }
    });

    const filterObj = hasArrHaveAttentionToVal
        ? addressArr.filter((item: Address) => item.AttentionTo === ctx.requestContext.channel?.ChannelNaturalId)
        : addressArr;

    return filterObj || [];
}

/**
 * The getAddress data action
 * Gets a customers information via the read RetailServer API
 * Returns address information associated with the retrieved customer.
 */
export const getAddressActionDataAction = createObservableDataAction<Address[]>({
    id: '@msdyn365-commerce-modules/retail-actions/get-address',
    action: <IAction<Address[]>>getAddressAction,
    input: <(args: ICreateActionContext) => IActionInput>createGetAddressInput
});

export default getAddressActionDataAction;
